import { WritableSignal } from '@angular/core';
import { bearing, destination } from '@turf/turf';
import { GeoJSON } from 'leaflet';
import {
  defer,
  lastValueFrom,
  MonoTypeOperatorFunction,
  Observable,
  retry,
  switchMap,
  tap,
  timer,
} from 'rxjs';

export function validateAllowedOrigins(origin: string, allowedOrigin: string) {
  if (allowedOrigin?.includes('*')) {
    // process for wildcard

    // Escape regex special characters except '*'
    const escaped = allowedOrigin.replace(/[.+?^${}()|[\]\\]/g, '\\$&');

    // Replace '*' with a regex fragment matching any subdomain (or nothing)
    const regexString = '^' + escaped.replace(/\*/g, '.+') + '$';

    // Build the RegExp
    const urlRegex = new RegExp(regexString);

    if (urlRegex.test(origin)) {
      return origin;
    }
  } else if (allowedOrigin === origin) {
    return origin;
  }

  return null;
}

export function isNil(value: unknown): value is undefined | null {
  return value === undefined || value === null;
}

export function chunk<T>(list: T[], chunkSize: number): T[][] {
  const chunks: T[][] = [];
  if (chunkSize < 1) {
    return [];
  }

  for (let i = 0; i < list.length; i = i + chunkSize) {
    chunks.push(list.slice(i, i + chunkSize));
  }

  return chunks;
}

export function uniqueBy<T>(list: T[], key: keyof T): T[] {
  const seen = new Set();

  return list.filter((item) => {
    const value = item[key];
    if (value === undefined) {
      return true;
    }
    if (seen.has(value)) {
      return false;
    }
    seen.add(value);
    return true;
  });
}

export function unique<T>(list: T[]): T[] {
  const seen = new Set();

  return list.filter((value) => {
    if (value === undefined) {
      return true;
    }
    if (seen.has(value)) {
      return false;
    }
    seen.add(value);
    return true;
  });
}

export function hasUpperCase(str: string) {
  return str !== str.toLowerCase();
}

export function hasLowerCase(str: string) {
  return str !== str.toUpperCase();
}

export function hasNumbers(str: string) {
  return /\d/.test(str);
}

/**
 * Test allowed special characters in a Cognito password
 *
 * Removed Lookahead with wildcard to prevent ReDoS attacks according to SonarQ
 * https://docs.aws.amazon.com/cognito/latest/developerguide/managing-users-passwords.html#user-pool-settings-policies
 * @param str
 */
export function hasSpecialChars(str: string) {
  return /[\^$*.[\]{}()?"!@#%&/\\,><':;|_~`=+\- ]/.test(str);
}

export function processMobileNumber(phoneNumber: string) {
  const trimmedNumber = phoneNumber.split(/\s/).join('');

  return trimmedNumber.startsWith('04')
    ? '+61' + trimmedNumber.slice(1)
    : trimmedNumber;
}

export function groupBy<T, U extends PropertyKey>(
  elements: T[],
  selector: (value: T) => U,
): Record<U, T[]> {
  const group = {} as Record<U, T[]>;
  for (const element of elements) {
    const key = selector(element);
    group[key] ??= [];
    group[key].push(element);
  }
  return group;
}

export function mapOf<T, U>(
  elements: T[],
  selector: (value: T) => U,
): Map<U, T> {
  const group = new Map<U, T>();
  for (const element of elements) {
    const key = selector(element);
    group.set(key, element);
  }
  return group;
}

export async function fromAsync<T>(iterable: AsyncIterable<T>): Promise<T[]> {
  const items = [] as T[];

  for await (const item of iterable) {
    items.push(item);
  }

  return items;
}

export const isEmptyInputStr = (inputStr?: string | null): boolean => {
  return typeof inputStr !== 'string' || inputStr.trim() === '';
};

export const isWhitespace = (char: string): boolean => {
  return /^\s$/.test(char);
};

export const isLetter = (char: string): boolean => {
  return /^[A-Za-z]$/.test(char);
};

export const isDigit = (char: string): boolean => {
  return /^\d$/.test(char);
};

/**
 * Extracts an interior string from another string.
 * Declared to be copied from ArcIMSToolKit by @Midler
 *
 * @param inString String to be used to extract interior string.
 * @param beforeString String preceding starting position of interior string.
 * @param afterString String following ending position of interior string.
 * @param startpos Numeric representation character position to begin search for beforeString. Use zero to search from beginning position.
 * @param limitpos Numeric representation character that beforeString must precede. If no limit is imposed, zero is used.
 * @param caseSensitive Boolean indicating if search will be case-sensitive.
 * @return Interior String.
 */
export const getInsideString = (
  inString: string,
  beforeString: string,
  afterString: string,
  startpos = 0,
  limitpos = 0,
  caseSensitive = true,
): string => {
  let returnString = '';
  let ucInString: string;
  let ucBefore: string;
  let ucAfter: string;
  let endpos = 0;
  let pos: number;

  if (limitpos === 0) {
    limitpos = inString.length;
  }

  if (!caseSensitive) {
    ucInString = inString.toUpperCase();
    ucBefore = beforeString.toUpperCase();
    ucAfter = afterString.toUpperCase();
  } else {
    ucInString = inString;
    ucBefore = beforeString;
    ucAfter = afterString;
  }

  pos = ucInString.indexOf(ucBefore, startpos);
  if (pos !== -1 && pos < limitpos) {
    pos = pos + ucBefore.length;
    endpos = ucInString.indexOf(ucAfter, pos);

    if (endpos !== -1) {
      returnString = inString.substring(pos, endpos);
    } else {
      returnString = inString.substring(pos, limitpos);
    }
  }

  return returnString;
};

/**
 * Adapted from https://gis.stackexchange.com/a/359643
 * @param geometry
 */
export function offsetSurveyLabel(
  geometry: GeoJSON.LineString,
): GeoJSON.LineString {
  const startPoint = geometry.coordinates[0];
  const endPoint = geometry.coordinates[1];

  const b = bearing(startPoint, endPoint);
  const offset = 0.005;
  const newStartPoint = destination(startPoint, offset, b - 90).geometry
    .coordinates;
  const newEndPoint = destination(endPoint, offset, b - 90).geometry
    .coordinates;

  return {
    ...geometry,
    coordinates: [newStartPoint, newEndPoint],
  };
}

/**
 * Converts degrees, minutes, and seconds to decimal degrees
 * @param degrees - The degrees component
 * @param minutes - The minutes component
 * @param seconds - The seconds component
 * @param precision - optional precision for the decimal value
 * @returns The decimal degree value
 */
export function convertDMSToDecimal(
  degrees: string,
  minutes: string,
  seconds: string,
  precision?: number,
): number {
  const degreeValue = parseFloat(degrees);
  const isNegative = degreeValue < 0;

  const absDegrees = Math.abs(degreeValue);
  const min = Math.abs(parseFloat(minutes));
  const sec = Math.abs(parseFloat(seconds));

  if (min >= 60 || sec >= 60) {
    throw new Error('Minutes or seconds must be less than 60.');
  }

  let decimalValue = absDegrees + min / 60 + sec / 3600;
  if (precision) {
    const factor = Math.pow(10, precision);
    decimalValue = Math.round(decimalValue * factor) / factor;
  }
  return isNegative ? -decimalValue : decimalValue;
}

/**
 * Parses a DMS (Degrees, Minutes, Seconds) string into its components
 * Supports formats like:
 * - "37° 49' 39.4'"
 * - "-37° 49' 39.4'"
 *
 * @param dmsString - The DMS string to parse
 * @returns An object containing the degrees, minutes, and seconds as strings
 * @throws Error if the string cannot be parsed as a valid DMS format
 */
export function parseDMSString(dmsString: string): {
  degrees: string;
  minutes: string;
  seconds: string;
} {
  const dmsRegex =
    /^([+-]?\d+(?:\.\d+)?)[°\s]+(\d{1,2}(?:\.\d+)?)['\s]+(\d{1,2}(?:\.\d+)?)["']?$/;
  const match = dmsRegex.exec(dmsString.trim());
  if (!match) {
    throw new Error(`Invalid DMS format: ${dmsString}`);
  }
  return {
    degrees: match[1],
    minutes: match[2],
    seconds: match[3],
  };
}

/**
 * Converts a DMS (Degrees, Minutes, Seconds) string to decimal degrees
 *
 * @param dmsString - The DMS string to convert
 * @param precision - Optional precision for the decimal value
 * @returns The decimal degree value
 * @throws Error if the string cannot be parsed or if minutes/seconds are invalid
 */
export function convertDMSStringToDecimal(
  dmsString: string | null,
  precision?: number,
): number {
  if (isNil(dmsString)) {
    throw new Error('DMS string cannot be Nil');
  }
  const { degrees, minutes, seconds } = parseDMSString(dmsString);
  return convertDMSToDecimal(degrees, minutes, seconds, precision);
}

/**
 *  Checks if a string is a valid date string in the formats:
 *  formats: YYYY, MM/YYYY, DD/MM/YYYY
 * @param dateString - The date string to validate
 * @returns true if the string is a valid date string, false otherwise
 */
export function isValidDateString(dateString: string): boolean {
  if (/^\d{4}$/.test(dateString)) {
    const year = parseInt(dateString, 10);
    return year >= 1000 && year <= 9999;
  }

  if (/^\d{1,2}\/\d{4}$/.test(dateString)) {
    const [monthStr, yearStr] = dateString.split('/');
    const month = parseInt(monthStr, 10);
    const year = parseInt(yearStr, 10);

    return year >= 1000 && year <= 9999 && month >= 1 && month <= 12;
  }

  if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dateString)) {
    const [dayStr, monthStr, yearStr] = dateString.split('/');
    const day = parseInt(dayStr, 10);
    const month = parseInt(monthStr, 10);
    const year = parseInt(yearStr, 10);

    if (!(year >= 1000 && year <= 9999)) return false;
    if (!(month >= 1 && month <= 12)) return false;

    const daysInMonth = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

    if (isLeapYear(year)) {
      daysInMonth[1] = 29;
    }

    return day >= 1 && day <= daysInMonth[month - 1];
  }

  return false;
}

export interface ParsedDate {
  day: number | null;
  month: number | null;
  year: number | null;
}

/**
 * Parses a flexible date string into day, month, and year components
 * Accepts formats: YYYY, MM/YYYY, DD/MM/YYYY
 *
 * @param dateString The date string to parse
 * @returns Object containing the parsed day, month, and year (null for missing parts)
 */
export function parseFlexibleDate(dateString: string): ParsedDate {
  const result: ParsedDate = {
    day: null,
    month: null,
    year: null,
  };

  if (!dateString || !isValidDateString(dateString.trim())) {
    return result;
  }

  const parts = dateString.trim().split('/');

  if (parts.length === 1) {
    // Year only: YYYY
    result.year = parseInt(parts[0], 10);
  } else if (parts.length === 2) {
    // Month/Year: MM/YYYY
    result.month = parseInt(parts[0], 10);
    result.year = parseInt(parts[1], 10);
  } else if (parts.length === 3) {
    // Full date: DD/MM/YYYY
    result.day = parseInt(parts[0], 10);
    result.month = parseInt(parts[1], 10);
    result.year = parseInt(parts[2], 10);
  }

  return result;
}

function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

export function isEmpty(value: unknown): boolean {
  if (value == null) return false;
  if (typeof value === 'string') return value.trim().length === 0;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return Object.keys(value).length === 0;
  return false;
}

export function retryAsync<T>(
  fn: () => Promise<T>,
  options: { retryCount: number; interval: number; exponent?: number },
): Promise<T> {
  return lastValueFrom(
    defer(fn).pipe(
      retry({
        count: options.retryCount,
        delay: (_error, retryIndex) => {
          const interval = options.interval;
          const delay =
            Math.pow(options.exponent ?? 2, retryIndex - 1) * interval;
          return timer(delay);
        },
      }),
    ),
  );
}

export const loading = <T>(
  signal: WritableSignal<boolean>,
): MonoTypeOperatorFunction<T> =>
  tap<T>({
    subscribe: () => signal.set(true),
    finalize: () => signal.set(false),
  });

export function assertDefined<T>(
  value: T | undefined | null,
): asserts value is T {
  if (value == null) {
    throw new Error('Value is null or undefined');
  }
}

export interface PollOptions {
  timeout: number;
  interval: number;
}

export const poll = <T>(obs: Observable<T>, options?: Partial<PollOptions>) => {
  const { timeout = 0, interval = 5_000 } = options ?? {};

  return timer(timeout, interval).pipe(switchMap(() => obs.pipe(retry(5))));
};
